import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InfoRounded as InfoRoundedIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  ERRORS_REDEEM,
  REDEEM_CODE_PREFIX,
  TOP_UP_PACKAGE_CODE_REGEX,
} from '@src/constants/redeem';
import actions from '@src/redux/actions';
import ReferralPopup from '@src/components/ReferralPopup';
import apis from '@src/apis';
import moment from 'moment';
import { isValidHttpUrl, removeQueryParams } from '@src/utils/url';
import { FREE_PACKAGE_CODES, ORDER_STATUS } from '@src/constants/package';
import {
  AUTO_APPLY_REFERRAL_PARAM,
  REFERRAL_CODE_PARAM,
} from '@src/constants/referral';
import {
  extractReferralCodeFromUrl,
  getUrlRedeemCode,
} from '@src/services/referral';
import CommonButton from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import { delay } from '@src/utils/delay';
import route from '@src/constants/route';
import useHandleOrderError from '@src/hooks/useHandleOrderError';
import {
  StyledReferralDialogErrorMessage,
  StyledReferralDialogInput,
  StyledReferralDialogNote,
} from './index.style';
import RedeemDialogSuccess from './RedeemDialogSuccess';

const RedeemDialog = ({
  appliedReferralErrorMessage,
  referralCode,
  rewardCredits,
  openRewardDialog,
  setOpenRewardDialog,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const { handleOrderError } = useHandleOrderError();
  const { isOpeningRedeemReferralDialog, latestOrder, usingPackage } =
    useSelector((state) => state.user);
  const { user } = useSelector((state) => state.auth);
  const { packageExpiryDate } = user;

  const [
    isOpeningRedeemReferralDialogSuccess,
    setIsOpeningRedeemReferralDialogSuccess,
  ] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const dispatch = useDispatch();

  const [code, setCode] = useState(referralCode || '');
  const [credits, setCredits] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChangeCode = (e) => {
    setCode(e.target.value);
    setErrorMessage('');
  };

  const handlePaste = (e) => {
    // Prevent default paste behavior to avoid pasting the referral link from the clipboard
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    if (isValidHttpUrl(text)) {
      const codeFromUrl = extractReferralCodeFromUrl(text);
      setCode(codeFromUrl);
    } else {
      setCode(text);
    }
  };

  const handleClose = () => {
    setCode('');
    setErrorMessage('');
    dispatch(actions.user.updateIsOpeningRedeemReferralDialog(false));
    if (referralCode) {
      history.replace({
        pathname: location.pathname,
        search: removeQueryParams(location.search, [
          REFERRAL_CODE_PARAM,
          AUTO_APPLY_REFERRAL_PARAM,
        ]),
      });
    }
  };

  const checkExpiredDate = (expiryDate) => {
    if (!expiryDate) return false;
    return moment().isAfter(expiryDate);
  };

  const handleRedeemCode = async () => {
    setErrorMessage('');

    setIsRedeeming(true);
    try {
      const redeemCode = code?.trim();
      const response = await apis.redeem.checkRedeemCode(redeemCode);
      if (response?.isValid) {
        const packageExpired = checkExpiredDate(packageExpiryDate);
        const isFreePackage = FREE_PACKAGE_CODES.includes(usingPackage?.code);

        const hasOrderPending = latestOrder.status === ORDER_STATUS.PENDING;
        if (hasOrderPending) {
          setErrorMessage(t(ERRORS_REDEEM.HAS_ORDER_PENDING));
          setIsRedeeming(false);
          return;
        }

        const isTopUpReward = TOP_UP_PACKAGE_CODE_REGEX.test(
          response?.reward?.package?.code,
        );

        if (packageExpired || isFreePackage || isTopUpReward) {
          const createOrderFields = {
            redeemCode,
          };
          const data = await apis.orders.createOrder(createOrderFields);
          if (data?.status) {
            dispatch(actions.user.updateIsOpeningRedeemReferralDialog(false));
            history.push(route.TTS);
          } else {
            const messageErrorRedeem = ERRORS_REDEEM[data?.code];
            if (messageErrorRedeem) {
              setErrorMessage(t(messageErrorRedeem));
              return;
            }
            // Check for error code 6001 and show popup
            if (handleOrderError(data)) {
              handleClose();
              return;
            }
            dispatch(
              actions.noti.push({
                severity: 'error',
                message: 'planActivatedFailed',
              }),
            );
          }
        } else {
          const {
            code: packageCode,
            creditsPerCycle,
            credits: creditsInPackage,
            devices,
            professionalVoiceClones,
          } = response?.reward?.package || {};

          if (!packageCode) return;
          const paymentUrl = getUrlRedeemCode({
            packageCode,
            credits: creditsInPackage,
            devices,
            voiceClonings: professionalVoiceClones,
            redeemCode,
            creditsPerCycle,
          });
          window.location.href = paymentUrl;
          dispatch(actions.user.updateIsOpeningRedeemReferralDialog(false));
        }
      }
    } catch (error) {
      const errorCode = error.response?.data?.error?.code;
      setErrorMessage(t(ERRORS_REDEEM[errorCode]));
    } finally {
      setIsRedeeming(false);
    }
  };

  const handleConfirm = async () => {
    const redeemCode = code
      ?.trim()
      ?.toUpperCase()
      .startsWith(REDEEM_CODE_PREFIX);
    if (redeemCode) {
      await handleRedeemCode();
      return;
    }
    // Check user eligibility before apply referral
    const resultCheckEligibility = await apis.referral.checkUserEligibility();
    if (!resultCheckEligibility.status) {
      setCode('');
      setIsRedeeming(false);
      dispatch(actions.user.openReferralErrorNotiPopup());
      dispatch(actions.user.updateIsOpeningRedeemReferralDialog(false));
      return;
    }

    // Apply referral
    const response = await apis.referral.applyReferral(code);
    if (response.status) {
      // Add delay after success to make sure backend has correctly updated user characters.
      await delay(2000);
      dispatch(actions.user.updateIsOpeningRedeemReferralDialog(false));
      setIsOpeningRedeemReferralDialogSuccess(true);
      const refereeReward = response?.result?.refereeRewards?.find(
        (reward) => reward.type === 'character',
      );
      setCredits(refereeReward?.details?.characters || 0);
      dispatch(actions.auth.updateReferrerId(response?.result?.referrerId));
    } else {
      setErrorMessage(t('invalidReferralCode'));
    }
    setIsRedeeming(false);
  };

  const handleCloseRedeemReferralDialogSuccess = async () => {
    setIsOpeningRedeemReferralDialogSuccess(false);
    setOpenRewardDialog(false);
    // This is for update user info after redeem referral code
    // to make sure user can see the correct total characters
    const userRes = await apis.account.getMe();
    dispatch(actions.auth.updateUserInfo(userRes.result));
  };

  const renderContent = () => (
    <>
      <StyledReferralDialogInput
        variant="filled"
        fullWidth
        value={code}
        onChange={handleChangeCode}
        placeholder={t('placeHolderRedeemYourCode')}
        onPaste={handlePaste}
      />
      {errorMessage && (
        <StyledReferralDialogErrorMessage>
          {errorMessage}
        </StyledReferralDialogErrorMessage>
      )}
      <StyledReferralDialogNote>
        <InfoRoundedIcon className="note-icon" />
        <Typography className="note-text">{t('noteRedeemYourCode')}</Typography>
      </StyledReferralDialogNote>
    </>
  );

  const renderAction = () => (
    <CommonButton
      variant="contained"
      textProps={{
        variantText: 'body-14-semibold',
        color: COLOR.indigo.default,
      }}
      customStyle={{
        width: '115px',
        height: '40px',
        borderRadius: '8px',
      }}
      disabled={!code.trim() || isRedeeming}
      onClick={handleConfirm}
    >
      {isRedeeming ? (
        <CircularProgress
          value={75}
          className="circular-progress"
          size="24px"
        />
      ) : (
        t('confirm')
      )}
    </CommonButton>
  );

  useEffect(() => {
    if (appliedReferralErrorMessage) {
      setErrorMessage(appliedReferralErrorMessage);
    }
  }, [appliedReferralErrorMessage]);

  return (
    <>
      <ReferralPopup
        title={t('redeemYourCode')}
        contentComponent={renderContent()}
        actionComponent={renderAction()}
        open={isOpeningRedeemReferralDialog}
        onClose={handleClose}
      />
      <RedeemDialogSuccess
        open={isOpeningRedeemReferralDialogSuccess || openRewardDialog}
        onClose={handleCloseRedeemReferralDialogSuccess}
        credits={credits || rewardCredits}
        referralCode={referralCode}
      />
    </>
  );
};

export default RedeemDialog;
